import { elapsedMs, formatDuration, type GameState, isComplete, type Level, levelName } from '@arrow/game-engine';
import type { PuzzleType } from '@arrow/puzzle-schema';
import { type ReactNode, useEffect, useState } from 'react';

import { useSettings } from '../app/settings.ts';
import { dayMonth, weekdayName } from '../game/view.ts';
import { BackPill, type BackTo } from './BackPill.tsx';
import { Flame, Pumpkin, Tree } from './icons.tsx';

interface HeaderProps {
	number: number | null;
	title: string;
	/** A special puzzle's type (halloween): a seasonal chip on the day row. */
	season?: PuzzleType | undefined;
	/** The puzzle's date (or the player's day, for a practice puzzle). */
	dateKey: string;
	game: GameState;
	streak: number;
	/** The ≡ menu (AppMenu), top right. */
	menu: ReactNode;
	/** Big boards only: the camera's two zoom levels, on a tactile button. */
	zoom?: { whole: boolean; onToggle: () => void } | undefined;
	/**
	 * Big boards: one line (mark and wordmark, the day in soft italic, the clock, the
	 * zoom key and the menu), so the board gets the height. The date line goes to
	 * screen readers only.
	 */
	compact?: boolean | undefined;
	/** The daily's (or practice puzzle's) level, set after the number: "No. 42 · Hard". */
	level?: Level | undefined;
	/** An older daily, played from the archive: labelled quietly, never for the streak. */
	archive?: boolean | undefined;
	/** "‹ Today" (a daily) or "‹ Play" (a practice puzzle), top left, where the wordmark was. */
	back?: BackTo | undefined;
	/** A challenge game: the pace ghost, a slim track under the masthead. */
	pace?: ReactNode;
	/** The player's day: an archive date from another year carries its year. */
	todayKey?: string | undefined;
	/**
	 * The wide game (J3, from 960px with a mouse): one row in a full-width band, the way back,
	 * the day and its line, then the clock, the streak and ≡.
	 */
	wide?: boolean | undefined;
}

/**
 * The masthead, kept restrained so the puzzle is the hero: "‹ Today" (the way home) with
 * the streak and ≡, an ink rule, then the day leading in soft italic over a quiet date
 * line, the clock set apart at its right. Sentence case throughout. Big boards use the
 * one-line `compact` masthead. With the clock off in Settings, the running clock is
 * hidden; a finished puzzle still shows its time.
 */
export function Header({ number, title, dateKey, game, streak, menu, zoom, compact, level, archive, back = '/', pace, todayKey, wide, season }: HeaderProps) {
	const { clock } = useSettings();
	const timer = clock || isComplete(game) ? <Timer game={game} /> : null;
	const day = weekdayName(dateKey);
	const lv = level ? <> · {levelName(level)}</> : null;
	// The day is the heading above it, so the line is the date and number, as on today's
	// masthead; an earlier year says so (QA3-9).
	const year = dateKey.slice(0, 4);
	const thisYear = todayKey?.slice(0, 4) ?? String(new Date().getFullYear());
	const when = archive && year !== thisYear ? `${dayMonth(dateKey)} ${year}` : dayMonth(dateKey);
	const dateLine =
		number !== null ? (
			<>
				{when}, No.&nbsp;{number}
				{lv}
			</>
		) : (
			<>
				{title}
				{lv}
			</>
		);
	// The page's heading, for screen readers: the wedge pill took the wordmark's place. Three
	// puzzles share a number, so it names the level too, as the grid does (UX-038).
	const named = level ? `, ${levelName(level)}` : '';
	const heading = <h1 className="sr-only">{number !== null ? `Sparrow No. ${number}${named}` : `Sparrow: ${title}${named}`}</h1>;
	// A seasonal puzzle (by its type) shows a festive chip in the date line's place.
	const seasonPill =
		season === 'halloween' ? (
			<span className="season-pill">
				<Pumpkin />
				Halloween
			</span>
		) : season === 'christmas' ? (
			<span className="season-pill">
				<Tree />
				Christmas
			</span>
		) : null;
	// The seasonal puzzle is titled after its theme; the chip already says so, so it takes the date line's place.
	const metaLead = seasonPill ?? <span className="meta-date">{dateLine}</span>;
	const streakBadge = streak > 0 && (
		<span className="streak" role="img" aria-label={`${streak}-day streak`}>
			<Flame />
			<span aria-hidden="true">{streak}</span>
		</span>
	);
	const fromArchive = archive && number !== null;
	// From the archive (J9): the chip says where it's from, and a screen reader hears why it
	// matters. No label of its own: "No streak" read as "you have no streak".
	const archiveChip = fromArchive && (
		<span className="arch-pill chip info sm">
			<BookIcon />
			From the archive
			<span className="sr-only">. {NOT_FOR_STREAK}</span>
		</span>
	);
	if (wide) {
		return (
			<header className="hdr wide">
				<div className="hdr-top">
					{heading}
					<BackPill to={back} />
					<div className="hdr-title">
						<span className="day">{day}</span>
						<span className="meta">
							{metaLead}
						</span>
						{archiveChip}
					</div>
					<div className="hdr-r">
						{timer}
						{streakBadge}
						{zoom && <ZoomButton whole={zoom.whole} onToggle={zoom.onToggle} />}
						{menu}
					</div>
				</div>
				{pace}
			</header>
		);
	}
	if (compact) {
		return (
			<header className="hdr compact">
				<div className="hdr-top">
					{heading}
					<BackPill to={back} />
					<p className="day" title={fromArchive ? `From the archive · No. ${number}` : undefined}>
						{day}
						<span className="sr-only">
							, {fromArchive ? <>from the archive, </> : null}
							{dateLine}
							{fromArchive ? <>. {NOT_FOR_STREAK}</> : null}
						</span>
					</p>
					{seasonPill}
					<div className="hdr-r">
						{streakBadge}
						{timer}
						{zoom && <ZoomButton whole={zoom.whole} onToggle={zoom.onToggle} />}
						{menu}
					</div>
				</div>
				{pace}
			</header>
		);
	}
	return (
		<header className="hdr">
			<div className="hdr-top">
				{heading}
				<BackPill to={back} />
				<div className="hdr-r">
					{streakBadge}
					{zoom && <ZoomButton whole={zoom.whole} onToggle={zoom.onToggle} />}
					{menu}
				</div>
			</div>
			{/* An archive day (docs/mockups/play-2.html): the day with a quiet chip saying where
			    it's from (an info chip, not a button: UX-010), then the same line as today's. */}
			<div className={fromArchive ? 'hdr-day archive' : 'hdr-day'}>
				<span className="day">{day}</span>
				{archiveChip}
				<span className="meta">
					{metaLead}
					{timer}
				</span>
			</div>
			{pace}
		</header>
	);
}

/** Why an archive day is marked, for screen readers (the chip's words; J9). */
export const NOT_FOR_STREAK = 'It doesn’t count towards your streak.';

/** An open book: "from the archive". */
function BookIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true" className="arch-book">
			<path d="M4 5.5C7 4.5 10 4.8 12 6.5c2-1.7 5-2 8-1v13c-3-1-6-.7-8 1-2-1.7-5-2-8-1z" />
			<path d="M12 6.5v13" />
		</svg>
	);
}

/**
 * Whole board ⇄ reading size. Four corners pointing out (zoom out to the whole board)
 * or in (back to reading size); the label says which.
 */
function ZoomButton({ whole, onToggle }: { whole: boolean; onToggle: () => void }) {
	return (
		<button
			type="button"
			className="zoom-btn"
			aria-label={whole ? 'Zoom back in' : 'Zoom out to the whole board'}
			onClick={onToggle}
		>
			<svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" aria-hidden="true">
				<path d={whole ? 'M4 9h5V4M20 9h-5V4M4 15h5v5M20 15h-5v5' : 'M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5'} />
			</svg>
		</button>
	);
}

/** The active-time clock. Re-renders itself once a second; nothing else does. */
function Timer({ game }: { game: GameState }) {
	const running = game.runningSince !== null;
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		setNow(Date.now());
		if (!running) return;
		const id = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(id);
	}, [running, game]);
	const text = formatDuration(elapsedMs(game, now));
	return (
		<span className="time" role="timer" aria-label={`Time ${text}`}>
			{text}
		</span>
	);
}
